import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Save, Users, Building2, Shield, UserCog } from 'lucide-react';
import UserManagementTable from '../components/UserManagementTable';
import RoleManagement from './admin/RoleManagement';
import UserRoleAssignment from './admin/UserRoleAssignment';

const AdminManagement = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState(null);
  
  // Company settings
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    total_employees: 100,
    industry: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    idle_timeout_minutes: 15,
    // India Form 18 specific fields
    factory_license_no: '',
    manager_name: ''
  });

  // Users
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'reporter',
    department: ''
  });

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') {
      fetchCompanySettings();
      if (activeTab === 'users') {
        fetchUsers();
      }
    }
  }, [activeTab, user]);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/company/settings`);
      if (response.data) {
        setCompanySettings(response.data);
      }
    } catch (err) {
      console.log('No company settings yet');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
  
      if (Array.isArray(detail)) {
        setError(detail);
      } else {
        setError('Failed to fetch users');
      }
    };
  };

  const handleSaveCompanySettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(`${API}/company/settings`, companySettings);
      setSuccess('Company settings updated successfully! Reloading page to apply changes...');
      // Reload page after 2 seconds to apply new idle timeout setting
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      
      if (Array.isArray(detail)) {
        setError(detail);
      } else {
        setError(detail || 'Failed to update settings');
      }
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) {
      setError('Name and email are required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Generate a default password
      const defaultPassword = 'SafetyPass123!';
      const userPayload = {
        name: newUser.name,
        email: newUser.email,
        password: defaultPassword,
        role: newUser.role
      };
      
      await axios.post(`${API}/auth/register`, userPayload);
      setSuccess(`User added successfully! Default password: ${defaultPassword}`);
      setShowAddUser(false);
      setNewUser({ name: '', email: '', role: 'reporter', department: '' });
      fetchUsers();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      const detail = err.response?.data?.detail;
  
      if (Array.isArray(detail)) {
        setError(detail);
      } else {
        setError(detail || 'Failed to add user');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check for both legacy (lowercase) and new (capitalized) role names
  const isAdmin = user?.role === 'admin' || user?.role === 'Admin';
  const isHSEManager = user?.role === 'hse_manager' || user?.role === 'HSE Manager';
  
  if (!isAdmin && !isHSEManager) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive">
            <AlertDescription>You don't have permission to access this page.</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Admin Management</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('company')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'company'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Building2 className="w-5 h-5" />
            Company Settings
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'users'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Users className="w-5 h-5" />
            User Management
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'roles'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Shield className="w-5 h-5" />
            Role Management
          </button>
          <button
            onClick={() => setActiveTab('user-roles')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'user-roles'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <UserCog className="w-5 h-5" />
            User Roles
          </button>
        </div>

        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Render errors properly */}
        {error && (
          <Alert className="mb-6" variant="destructive">
            <AlertDescription>
              {Array.isArray(error) ? (
                <div className="space-y-1">
                  {error.map((err, idx) => (
                    <div key={idx}>
                      {err.loc && <strong>{err.loc[err.loc.length - 1]}: </strong>}
                      {err.msg}
                    </div>
                  ))}
                </div>
              ) : (
                error
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Company Settings Tab */}
        {activeTab === 'company' && (
          <Card>
            <CardHeader>
              <CardTitle>Company Information & Metrics Configuration</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                These settings are used to calculate safety metrics like LTIFR, TRIFR, and leading indicators
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company / Factory Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={companySettings.company_name}
                  onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter company or factory name"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used in statutory reports like India Form 18
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Total Employees <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  value={companySettings.total_employees}
                  onChange={(e) => setCompanySettings({...companySettings, total_employees: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used for LTIFR, TRIFR, and other rate calculations
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Industry
                </label>
                <select
                  value={companySettings.industry}
                  onChange={(e) => setCompanySettings({...companySettings, industry: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Industry</option>
                  <option value="construction">Construction</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="oil_gas">Oil & Gas</option>
                  <option value="mining">Mining</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="transportation">Transportation</option>
                  <option value="warehousing">Warehousing & Logistics</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Address
                </label>
                <textarea
                  value={companySettings.address}
                  onChange={(e) => setCompanySettings({...companySettings, address: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter company address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={companySettings.contact_email}
                    onChange={(e) => setCompanySettings({...companySettings, contact_email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="contact@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={companySettings.contact_phone}
                    onChange={(e) => setCompanySettings({...companySettings, contact_phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Session Timeout (minutes) <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  min="5"
                  max="120"
                  value={companySettings.idle_timeout_minutes}
                  onChange={(e) => setCompanySettings({...companySettings, idle_timeout_minutes: parseInt(e.target.value) || 15})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="15"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Users will be automatically logged out after this period of inactivity (SOC 2 CC6.1 compliance). Recommended: 15-30 minutes.
                </p>
              </div>

              {/* India Form 18 Specific Fields */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">India Statutory Compliance (Form 18)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  These fields are used to auto-populate India Form 18 statutory reports
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Factory License Number
                    </label>
                    <input
                      type="text"
                      value={companySettings.factory_license_no}
                      onChange={(e) => setCompanySettings({...companySettings, factory_license_no: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Factory license number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Factory Manager Name
                    </label>
                    <input
                      type="text"
                      value={companySettings.manager_name}
                      onChange={(e) => setCompanySettings({...companySettings, manager_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Name of factory manager"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSaveCompanySettings}
                disabled={loading}
                className="w-full md:w-auto"
              >
                {loading ? (
                  'Saving...'
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Company Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Users ({users.length})</h2>
              <Button onClick={() => setShowAddUser(!showAddUser)}>
                {showAddUser ? 'Cancel' : '+ Add User'}
              </Button>
            </div>

            {showAddUser && (
              <Card>
                <CardHeader>
                  <CardTitle>Add New User</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={newUser.name}
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="john@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Role <span className="text-red-600">*</span>
                      </label>
                      <select
                        value={newUser.role}
                        onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <optgroup label="System Roles">
                          <option value="Admin">Admin</option>
                          <option value="HSE Manager">HSE Manager</option>
                        </optgroup>
                        <optgroup label="Safety Roles">
                          <option value="Safety Manager">Safety Manager</option>
                          <option value="Safety Officer">Safety Officer</option>
                          <option value="Safety Coordinator">Safety Coordinator</option>
                        </optgroup>
                        <optgroup label="Operations Roles">
                          <option value="Supervisor">Supervisor</option>
                          <option value="Operator">Operator</option>
                          <option value="Technician">Technician</option>
                          <option value="Engineer">Engineer</option>
                        </optgroup>
                        <optgroup label="Management Roles">
                          <option value="Manager">Manager</option>
                          <option value="Director">Director</option>
                        </optgroup>
                        <optgroup label="Support Roles">
                          <option value="Contractor">Contractor</option>
                          <option value="Reporter">Reporter</option>
                        </optgroup>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select the appropriate role for this user
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Department
                      </label>
                      <input
                        type="text"
                        value={newUser.department}
                        onChange={(e) => setNewUser({...newUser, department: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Safety"
                      />
                    </div>
                  </div>

                  <Button onClick={handleAddUser} disabled={loading}>
                    {loading ? 'Adding...' : 'Add User'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {users.length > 0 && (
              <UserManagementTable
                users={users}
                onUpdate={fetchUsers}
              />
            )}
          </div>
        )}

        {/* Role Management Tab */}
        {activeTab === 'roles' && (
          <RoleManagement user={user} onLogout={onLogout} hideNavigation={true} />
        )}

        {/* User Roles Tab */}
        {activeTab === 'user-roles' && (
          <UserRoleAssignment user={user} onLogout={onLogout} hideNavigation={true} />
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
